import React from 'react';
import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import i18n from '../../i18n';
import apiManage, { apiManage_rbp } from '../../request';
import { getRBPStringId } from '../main/main-page-states';
import { parseRestaurantStaticBundled, withImageCacheBust } from '../../utils/restaurant-static-bundled';
import GlobalNotification from '../../component/common/snackbar';
import setLoading from '../../component/linear-determinate';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import { RestaurantStaticImageUpload } from '../../component/restaurant-static-image-upload.component';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import LSM from '../../modules/cache/tool-local-storage';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import SubFunctionProjectSettingSaveButton from './sub-function-project-setting-save-button.component';
import { SubFunctionAuxiliaryHint } from './sub-function-auxiliary-hint.component';
import { MOP_CAROUSEL_SETTING_KEYS } from './mop-setting-constants';
import { MopSettingPageSkeleton } from './sub-function-settings-skeletons.component';
import { IMAGE_UPLOAD_SPECS } from '../../utils/image-upload-spec';
import './scss/mop-setting.page.scss';

type Props = Record<string, never>;

type State = {
    /** 全页保存中 */
    isSaving: boolean;
    /** 下单自动送厨（设置 id 55） */
    autoDeliverToKitchen: boolean;
    /** 五个轮播位：槽位下标 0–4 对应 `POST/DELETE .../restaurant-static/bulletin-mop-carousel-image/{index}`，与设置 56–60 一一对应 */
    carouselSlots: { settingKey: (typeof MOP_CAROUSEL_SETTING_KEYS)[number]; pathValue: string }[];
    /** 轮播图 URL 缓存破坏（bundled 更新后刷新） */
    carouselImageBust: number;
    /** 首包拉取完成前展示骨架 */
    initialPageDataReady: boolean;
};

/**
 * 从本地缓存读取静态资源根地址，与公告图等页一致。
 * @returns 服务器根 URL（可能为空）
 */
function readServerUrlBase(): string {
    const raw = LSM.get(LOCAL_STORAGE_KEYS.SERVER_URL);
    return typeof raw === 'string' ? raw.replace(/\/+$/, '') : '';
}

/**
 * 拼接轮播图展示 URL；空路径不展示图片。
 * @param pathValue - 设置接口返回的相对路径或完整 URL
 * @param serverBase - 静态资源根
 * @returns 可给 `AvatarCard` 的地址，无效时返回 `undefined`
 */
/**
 * 从设置项或 bundled 里的路径/完整 URL 中解析 MOP 轮播 OSS 文件名，供 DELETE 的 query `file_name` 使用（形如 `0_<uuid>.jpg`）。
 * @param pathValue - `getSettingById` / bundled 中的字符串
 * @param slotIndex - 槽位 0–4，须与文件名前缀 `{index}_` 一致
 * @returns 合法 basename，无法解析时 `undefined`
 */
function extractMopCarouselFileNameFromPathValue(pathValue: string, slotIndex: number): string | undefined {
    const trimmed = String(pathValue || '').trim();
    if (!trimmed) {
        return undefined;
    }
    let pathPart = trimmed.split('?')[0];
    try {
        if (/^https?:\/\//i.test(pathPart)) {
            pathPart = new URL(pathPart).pathname;
        }
    } catch {
        // 非合法 URL 时按相对路径处理
    }
    const last = pathPart.split('/').filter(Boolean).pop() || '';
    const prefix = `${slotIndex}_`;
    if (last && last.toLowerCase().startsWith(prefix.toLowerCase())) {
        return last;
    }
    return undefined;
}

function buildCarouselImageUrl(pathValue: string, serverBase: string): string | undefined {
    const v = String(pathValue || '').trim();
    if (!v) {
        return undefined;
    }
    if (/^https?:\/\//i.test(v)) {
        return v;
    }
    if (!serverBase) {
        return undefined;
    }
    return `${serverBase}/${v}?randomId=${Math.random()}`;
}

/**
 * 云 RBP「子功能管理」— MOP 设置：下单自动送厨 + 首页五张轮播图（与 RBP2 `project-setting-mop` 语义一致，走 `apiManage_rbp`）。
 * 套餐组表格在 RBP2 中已注释，此处不实现；设计稿中的「首页轮播视频」不做。
 * 布局对齐 Figma 1897:22405 与 KWA/RWA 内容区。隧道校验在子功能壳页 `HookDecorator` 中统一完成。
 */
class MopSettingPage extends React.Component<Props, State> {
    /** 顶栏「辅助模式」：开启后在「下单自动送厨」下展示 Figma 说明文案（node 1897:22405） */
    static contextType = RbpCloudSubFunctionAuxiliaryModeContext;
    /** 由 `contextType` 注入 */
    context!: React.ContextType<typeof RbpCloudSubFunctionAuxiliaryModeContext>;

    /** 静态资源根，用于拼接轮播图地址 */
    private readonly serverUrlBase = readServerUrlBase();

    /** 防止保存重复提交 */
    private saveLocked = false;

    public constructor(props: Props) {
        super(props);
        this.state = {
            isSaving: false,
            autoDeliverToKitchen: false,
            carouselSlots: MOP_CAROUSEL_SETTING_KEYS.map((settingKey) => ({ settingKey, pathValue: '' })),
            carouselImageBust: 0,
            initialPageDataReady: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        await this.loadPageData();
    }

    /**
     * 拉取设置 55 与轮播图路径 56–60。
     */
    private loadPageData = async (): Promise<void> => {
        try {
            const sid = getRBPStringId();
            const allRes = await Promise.all([
                apiManage_rbp.getSettingById(55),
                ...MOP_CAROUSEL_SETTING_KEYS.map((k) => apiManage_rbp.getSettingById(k)),
                apiManage.get_restaurant_static_bundled_image_urls(sid),
            ]);
            const r55 = allRes[0];
            /** 设置 56–60 的轮播路径原始返回值 */
            const carouselRes = allRes.slice(1, 1 + MOP_CAROUSEL_SETTING_KEYS.length);
            /** bundled 原始 JSON（最后一项） */
            const bundledRaw = allRes[allRes.length - 1];
            const autoDeliverToKitchen = Boolean(_.get(r55, '[0].value.enable'));
            const bundledMop = parseRestaurantStaticBundled(bundledRaw).mopCarouselUrls;
            const carouselSlots = MOP_CAROUSEL_SETTING_KEYS.map((settingKey, i) => {
                const res = carouselRes[i];
                const fromSetting = String(_.get(res, '[0].value', '') ?? '');
                const fromBundled = String(bundledMop[i] ?? '').trim();
                /** 优先展示 bundled 中的完整 URL；兼容迁移期仍写在设置项里的相对路径 */
                const pathValue = fromBundled || fromSetting;
                return { settingKey, pathValue };
            });
            this.setState({ autoDeliverToKitchen, carouselSlots, carouselImageBust: Date.now() });
        } finally {
            this.setState({ initialPageDataReady: true });
        }
    };

    /**
     * 保存设置 id 55。
     */
    private persistSettings = async (): Promise<void> => {
        setLoading(true);
        const r = await apiManage_rbp.patchSettingById(55, { enable: this.state.autoDeliverToKitchen });
        if (_.get(r, 'error')) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } else {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        }
        setLoading(false);
    };

    /**
     * 点击保存。
     */
    private onClickSave = async (): Promise<void> => {
        if (this.saveLocked || this.state.isSaving) {
            return;
        }
        this.saveLocked = true;
        this.setState({ isSaving: true });
        try {
            await this.persistSettings();
        } finally {
            this.saveLocked = false;
            this.setState({ isSaving: false });
        }
    };

    /**
     * 上传指定槽位的轮播图（API 槽位下标 0–4）。
     * @param slotIndex - 槽位下标，与 RBP2 `onUploadScreenImg` 一致
     * @param _url - 裁剪后的预览地址（未使用）
     * @param file - 上传文件
     */
    private onUploadCarousel = async (slotIndex: number, _url: string, file: File): Promise<void> => {
        const upLoadRes = await apiManage_rbp.postMopCarouselImg({
            index: String(slotIndex),
            image: file,
        });
        if (!_.get(upLoadRes, 'error')) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } else {
            GlobalNotification.error(i18n.t('menu_and_dishes_page_upload_failed'));
        }
        await this.loadPageData();
    };

    /**
     * 删除指定槽位的轮播图。
     * @param slotIndex - 槽位下标 0–4
     */
    private onDeleteCarousel = async (slotIndex: number): Promise<void> => {
        const pathValue = this.state.carouselSlots[slotIndex]?.pathValue;
        const fileName = extractMopCarouselFileNameFromPathValue(String(pathValue || ''), slotIndex);
        if (!fileName) {
            GlobalNotification.error(i18n.t('google_text_delete_failed'));
            return;
        }
        const deleteRes = await apiManage_rbp.deleteMopCarouselImg(slotIndex, fileName);
        if (!_.get(deleteRes, 'error')) {
            GlobalNotification.success(i18n.t('google_text_delete_success'));
        } else {
            GlobalNotification.error(i18n.t('google_text_delete_failed'));
        }
        await this.loadPageData();
    };

    /**
     * 辅助模式下展示「下单自动送厨」的设计说明（与 Figma 1897:22405 次要文案一致）。
     */
    private renderAutoDeliverAuxiliaryHint(): React.ReactNode {
        return (
            <SubFunctionAuxiliaryHint
                enabled={!!this.context?.auxiliaryModeEnabled}
                i18nKey="mop_setting_page_auto_deliver_aux_hint"
                className="mop-setting-page__aux-hint"
            />
        );
    }

    public render(): React.ReactNode {
        if (!this.state.initialPageDataReady) {
            return <MopSettingPageSkeleton />;
        }
        const { isSaving, autoDeliverToKitchen, carouselSlots, carouselImageBust } = this.state;
        /** 已上传轮播图数量，用于标题旁「已填/总数」展示（与 Figma 首页轮播图旁 4/6 一致，本页共 5 槽位） */
        const carouselFilledCount = carouselSlots.filter((s) => String(s.pathValue || '').trim()).length;
        const carouselTotal = MOP_CAROUSEL_SETTING_KEYS.length;
        return (
            <Box className="mop-setting-page">
                {/* 与云 RBP 内容区 Figma 1861:53430 一致：浅灰底 + 圆角 16 + 投影 */}
                <Box className="mop-setting-page__surface">
                    <Box className="mop-setting-page__header">
                        <Typography variant="h4" color="primary" className="mop-setting-page__page-title">
                            {i18n.t('mop_setting_page_heading')}
                        </Typography>
                        <SubFunctionProjectSettingSaveButton loading={isSaving} onClick={this.onClickSave} />
                    </Box>

                    {/* 设计稿（Figma 1897:22405）：基础设置与首页轮播图为同一白卡，中间无横线分隔 */}
                    <Box className="mop-setting-page__card">
                        <Typography
                            variant="body1"
                            color="primary"
                            className="mop-setting-page__section-title"
                            fontWeight={700}
                        >
                            {i18n.t('mop_setting_page_basic_section_title')}
                        </Typography>
                        <Box className="mop-setting-page__auto-deliver-block">
                            <Box className="mop-setting-page__switch-row">
                                <Typography variant="body1" color="text.primary" component="span">
                                    {i18n.t('mop_setting_page_order_then_auto_deliver')}
                                </Typography>
                                <IOSSwitch
                                    checked={autoDeliverToKitchen}
                                    onChange={() => this.setState({ autoDeliverToKitchen: !autoDeliverToKitchen })}
                                    inputProps={{ 'aria-label': i18n.t('mop_setting_page_order_then_auto_deliver') }}
                                />
                            </Box>
                            {this.renderAutoDeliverAuxiliaryHint()}
                        </Box>

                        <Box className="mop-setting-page__carousel-block">
                            <Box className="mop-setting-page__carousel-section-head" component="h2">
                                <Typography variant="body1" color="primary" className="mop-setting-page__section-title" fontWeight={700} component="span">
                                    {i18n.t('mop_setting_page_main_page_slideshow')}
                                </Typography>
                                <Typography variant="body1" color="primary" fontWeight={700} component="span">
                                    {i18n.t('mop_setting_page_carousel_filled_total', {
                                        filled: carouselFilledCount,
                                        total: carouselTotal,
                                    })}
                                </Typography>
                            </Box>
                            <SubFunctionAuxiliaryHint
                                enabled={!!this.context?.auxiliaryModeEnabled}
                                i18nKey="mop_setting_aux_carousel_section_hint"
                                className="mop-setting-page__aux-hint mop-setting-page__aux-hint--carousel"
                            />
                            <Box className="mop-setting-page__carousel">
                            {carouselSlots.map((slot, slotIndex) => {
                                const built = buildCarouselImageUrl(slot.pathValue, this.serverUrlBase);
                                const trimmed = String(slot.pathValue || '').trim();
                                /** bundled 多为绝对 URL，追加 `cb`；相对路径仍走 `buildCarouselImageUrl` */
                                const avatarUrl =
                                    built && /^https?:\/\//i.test(trimmed)
                                        ? withImageCacheBust(trimmed.split('?')[0], carouselImageBust)
                                        : built;
                                return (
                                    <Box className="mop-setting-page__carousel-item" key={slot.settingKey}>
                                        <RestaurantStaticImageUpload
                                            stringId={getRBPStringId()}
                                            kind="dish-image"
                                            uploadEntityId={1}
                                            deferStaticUpload
                                            imageRef={trimmed}
                                            displayUrl={String(avatarUrl ?? '')}
                                            onStagedStaticFile={async (file) => {
                                                await this.onUploadCarousel(slotIndex, '', file);
                                            }}
                                            onDeferredStaticRemove={async () => {
                                                await this.onDeleteCarousel(slotIndex);
                                            }}
                                            uploadEmptyVariant="firstScreenGallery"
                                            uploadSpec={IMAGE_UPLOAD_SPECS.mopCarousel}
                                            slotWidth={256}
                                            slotHeight={160}
                                            sx={{ minHeight: 160 }}
                                        />
                                    </Box>
                                );
                            })}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default MopSettingPage;
